// Audit Log Viewer — Wired to all 4 backend endpoints
import {
  ClipboardDocumentListIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import api from '../../services/api';

/* ─── Types ─── */
interface AuditLog {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  changes: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface AuditStats {
  total_logs: number;
  actions_breakdown: Record<string, number>;
  entity_breakdown: Record<string, number>;
  active_users: number;
}

/* ─── Component ─── */
const AuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [page, setPage] = useState(1);
  const limit = 25;

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterAction, filterEntity]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set('skip', String((page - 1) * limit));
      params.set('limit', String(limit));
      if (filterAction) params.set('action', filterAction);
      if (filterEntity) params.set('entity_type', filterEntity);

      const [logData, statsData] = await Promise.all([
        api.get<AuditLog[]>(`/audit-logs?${params.toString()}`),
        api.get<AuditStats>('/audit-logs/stats/summary'),
      ]);

      setLogs(logData);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError('Failed to load audit logs. You may not have permission.');
    } finally {
      setLoading(false);
    }
  };

  const loadLogDetail = async (logId: string) => {
    try {
      const detail = await api.get<AuditLog>(`/audit-logs/${logId}`);
      setSelectedLog(detail);
    } catch {
      // Fallback to the log we already have
      const existing = logs.find((l) => l.id === logId);
      if (existing) setSelectedLog(existing);
    }
  };

  const fmtDate = (s: string) =>
    new Date(s).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

  const actionColor = (action: string): string => {
    const colors: Record<string, string> = {
      CREATE: 'bg-green-100 text-green-800',
      UPDATE: 'bg-blue-100 text-blue-800',
      DELETE: 'bg-red-100 text-red-800',
      LOGIN: 'bg-purple-100 text-purple-800',
      LOGOUT: 'bg-gray-100 text-gray-600',
      EXPORT: 'bg-yellow-100 text-yellow-800',
    };
    return colors[action.toUpperCase()] || 'bg-gray-100 text-gray-800';
  };

  const filteredLogs = searchUser
    ? logs.filter(
        (l) =>
          l.user_email?.toLowerCase().includes(searchUser.toLowerCase()) ||
          l.user_name?.toLowerCase().includes(searchUser.toLowerCase())
      )
    : logs;

  if (loading && logs.length === 0)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading audit logs...</div>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 mb-2">{error}</div>
          <button onClick={loadData} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="text-gray-600">Track all system actions for compliance and security</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <ClipboardDocumentListIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Events</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total_logs.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <FunnelIcon className="h-8 w-8 text-green-500 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Active Users</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active_users}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <ClipboardDocumentListIcon className="h-8 w-8 text-purple-500 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Action Types</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.keys(stats.actions_breakdown || {}).length}
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <ClipboardDocumentListIcon className="h-8 w-8 text-orange-500 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Entity Types</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.keys(stats.entity_breakdown || {}).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4 p-4">
          <div className="flex items-center space-x-2">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              placeholder="Search by user..."
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm w-48"
            />
          </div>
          <select
            value={filterAction}
            onChange={(e) => {
              setFilterAction(e.target.value);
              setPage(1);
            }}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">All Actions</option>
            {stats &&
              Object.keys(stats.actions_breakdown || {}).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
          </select>
          <select
            value={filterEntity}
            onChange={(e) => {
              setFilterEntity(e.target.value);
              setPage(1);
            }}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">All Entity Types</option>
            {stats &&
              Object.keys(stats.entity_breakdown || {}).map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
          </select>
          {(filterAction || filterEntity || searchUser) && (
            <button
              onClick={() => {
                setFilterAction('');
                setFilterEntity('');
                setSearchUser('');
                setPage(1);
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Logs Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Entity
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  IP Address
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(log.created_at)}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">{log.user_name || 'System'}</div>
                    <div className="text-gray-500 text-xs">{log.user_email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${actionColor(log.action)}`}
                    >
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <div>{log.entity_type}</div>
                    <div className="text-gray-400 text-xs truncate max-w-[120px]">
                      {log.entity_id}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{log.ip_address || '—'}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <button
                      onClick={() => loadLogDetail(log.id)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredLogs.length === 0 && (
          <div className="text-center py-12">
            <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No audit logs found</h3>
            <p className="text-gray-500">Try adjusting your filters or check back later.</p>
          </div>
        )}
        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {page} · Showing {filteredLogs.length} results
          </div>
          <div className="flex space-x-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="btn-secondary text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              disabled={logs.length < limit}
              onClick={() => setPage((p) => p + 1)}
              className="btn-secondary text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Audit Log Detail</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">
                    Action
                  </label>
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full mt-1 ${actionColor(selectedLog.action)}`}
                  >
                    {selectedLog.action}
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Date</label>
                  <p className="text-sm text-gray-900 mt-1">{fmtDate(selectedLog.created_at)}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">User</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedLog.user_name}</p>
                  <p className="text-xs text-gray-500">{selectedLog.user_email}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">
                    Entity
                  </label>
                  <p className="text-sm text-gray-900 mt-1">{selectedLog.entity_type}</p>
                  <p className="text-xs text-gray-500 truncate">{selectedLog.entity_id}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">
                    IP Address
                  </label>
                  <p className="text-sm text-gray-900 mt-1">{selectedLog.ip_address || '—'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">
                    User Agent
                  </label>
                  <p className="text-sm text-gray-900 mt-1 truncate">
                    {selectedLog.user_agent || '—'}
                  </p>
                </div>
              </div>
              {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-2">
                    Changes
                  </label>
                  <pre className="bg-gray-50 rounded-lg p-3 text-xs overflow-auto max-h-60">
                    {JSON.stringify(selectedLog.changes, null, 2)}
                  </pre>
                </div>
              )}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-2">
                    Metadata
                  </label>
                  <pre className="bg-gray-50 rounded-lg p-3 text-xs overflow-auto max-h-60">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => setSelectedLog(null)} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
